import { Component, DestroyRef, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { ROUTES } from '../../../core/constants/routes.constants';
import { CardComponent } from '../../../core/components/card/card.component';
import { RingProgressComponent } from '../../../core/components/ring-progress/ring-progress.component';
import { QuoteCardComponent } from '../../../core/components/quote-card/quote-card.component';
import { NavTileComponent } from '../../../core/components/nav-tile/nav-tile.component';
import { TranslatePipe } from '@ngx-translate/core';
import { HeroSlide, FeatureLink, AmaalBreakdown, ValueProp } from '../../interfaces/home.type';

@Component({
  selector: 'app-home',
  imports: [RouterLink, CardComponent, RingProgressComponent, QuoteCardComponent, NavTileComponent, TranslatePipe],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent {
  private readonly destroyRef = inject(DestroyRef);

  protected readonly routes = ROUTES;

  protected readonly heroSlides: HeroSlide[] = [
    { eyebrow: 'home.slide0.eyebrow', title: 'home.slide0.title', subtitle: 'home.slide0.subtitle', background: '/assets/images/BannerWeb.png' },
    { eyebrow: 'home.slide1.eyebrow', title: 'home.slide1.title', subtitle: 'home.slide1.subtitle', background: '/assets/images/BannerWeb.png' },
    { eyebrow: 'home.slide2.eyebrow', title: 'home.slide2.title', subtitle: 'home.slide2.subtitle', background: '/assets/images/BannerWeb.png' },
    { eyebrow: 'home.slide3.eyebrow', title: 'home.slide3.title', subtitle: 'home.slide3.subtitle', background: '/assets/images/BannerWeb.png' }
  ];

  protected readonly activeSlide = signal(0);

  constructor() {
    const intervalId = setInterval(() => this.nextSlide(), 6000);
    this.destroyRef.onDestroy(() => clearInterval(intervalId));
  }

  nextSlide(): void {
    this.activeSlide.update((index) => (index + 1) % this.heroSlides.length);
  }

  previousSlide(): void {
    this.activeSlide.update((index) => (index - 1 + this.heroSlides.length) % this.heroSlides.length);
  }

  goToSlide(index: number): void {
    this.activeSlide.set(index);
  }

  protected readonly nextPrayer = {
    name: 'Dhuhr',
    time: '12:20 PM',
    countdown: '01:45:32'
  };

  protected readonly features: FeatureLink[] = [
    { icon: '📅', title: 'home.features.amaal_planner.title', subtitle: 'home.features.amaal_planner.subtitle', path: ROUTES.AMAAL_PLANNER },
    { icon: '📿', title: 'home.features.duas_azkar.title', subtitle: 'home.features.duas_azkar.subtitle', path: ROUTES.DUAS_AZKAR },
    { icon: '📖', title: 'home.features.quran.title', subtitle: 'home.features.quran.subtitle', path: ROUTES.QURAN },
    { icon: '🧭', title: 'home.features.qibla.title', subtitle: 'home.features.qibla.subtitle', path: ROUTES.QIBLA_FINDER },
    { icon: '🕌', title: 'home.features.masjids.title', subtitle: 'home.features.masjids.subtitle', path: ROUTES.MASJIDS },
    { icon: '🕐', title: 'home.features.prayer_times.title', subtitle: 'home.features.prayer_times.subtitle', path: ROUTES.PRAYER_TIMES },
    { icon: 'الله', title: 'home.features.names.title', subtitle: 'home.features.names.subtitle', path: ROUTES.NAMES_OF_ALLAH },
    { icon: '⋯', title: 'home.features.more.title', subtitle: 'home.features.more.subtitle', path: ROUTES.MORE }
  ];

  protected readonly amaalProgress = 72;

  protected readonly amaalBreakdown: AmaalBreakdown[] = [
    { label: 'home.breakdown.fard', value: '5/5' },
    { label: 'home.breakdown.sunnah', value: '3/5' },
    { label: 'home.breakdown.nafl', value: '4/10' }
  ];

  protected readonly quote = {
    arabic: 'إِنَّ مَعَ الْعُسْرِ يُسْرًا',
    translation: 'Indeed, with hardship comes ease.',
    reference: 'Surah Ash-Sharh (94:6)'
  };

  protected readonly islamicDate = {
    day: 29,
    hijriMonthYear: 'Jumada Al-Awwal 1446 AH',
    gregorian: '1 June 2024',
    weekday: 'Saturday'
  };

  protected readonly continueReading = {
    surah: 'Surah Al-Kahf',
    verse: 'Verse 1',
    progress: 25
  };

  protected readonly valueProps: ValueProp[] = [
    { icon: '🤲', title: 'home.value_props.sincere_intention.title', subtitle: 'home.value_props.sincere_intention.subtitle' },
    { icon: '✅', title: 'home.value_props.consistent_actions.title', subtitle: 'home.value_props.consistent_actions.subtitle' },
    { icon: '📖', title: 'home.value_props.seek_knowledge.title', subtitle: 'home.value_props.seek_knowledge.subtitle' },
    { icon: '📿', title: 'home.value_props.remember_allah.title', subtitle: 'home.value_props.remember_allah.subtitle' }
  ];
}
